/**
 * @wharfkit/transact-plugin-explorerlink v0.3.0
 * https://github.com/wharfkit/transact-plugin-explorerlink
 *
 * @license
 * Copyright (c) 2021 FFF00 Agents AB & Greymass Inc. All Rights Reserved.
 * 
 * Redistribution and use in source and binary forms, with or without modification,
 * are permitted provided that the following conditions are met:
 * 
 *  1. Redistribution of source code must retain the above copyright notice, this
 *     list of conditions and the following disclaimer.
 * 
 *  2. Redistribution in binary form must reproduce the above copyright notice,
 *     this list of conditions and the following disclaimer in the documentation
 *     and/or other materials provided with the distribution.
 * 
 *  3. Neither the name of the copyright holder nor the names of its contributors
 *     may be used to endorse or promote products derived from this software without
 *     specific prior written permission.
 * 
 * THIS SOFTWARE IS PROVIDED BY THE COPYRIGHT HOLDERS AND CONTRIBUTORS "AS IS" AND
 * ANY EXPRESS OR IMPLIED WARRANTIES, INCLUDING, BUT NOT LIMITED TO, THE IMPLIED
 * WARRANTIES OF MERCHANTABILITY AND FITNESS FOR A PARTICULAR PURPOSE ARE DISCLAIMED.
 * IN NO EVENT SHALL THE COPYRIGHT HOLDER OR CONTRIBUTORS BE LIABLE FOR ANY DIRECT,
 * INDIRECT, INCIDENTAL, SPECIAL, EXEMPLARY, OR CONSEQUENTIAL DAMAGES (INCLUDING,
 * BUT NOT LIMITED TO, PROCUREMENT OF SUBSTITUTE GOODS OR SERVICES; LOSS OF USE,
 * DATA, OR PROFITS; OR BUSINESS INTERRUPTION) HOWEVER CAUSED AND ON ANY THEORY OF
 * LIABILITY, WHETHER IN CONTRACT, STRICT LIABILITY, OR TORT (INCLUDING NEGLIGENCE
 * OR OTHERWISE) ARISING IN ANY WAY OUT OF THE USE OF THIS SOFTWARE, EVEN IF ADVISED
 * OF THE POSSIBILITY OF SUCH DAMAGE.
 * 
 * YOU ACKNOWLEDGE THAT THIS SOFTWARE IS NOT DESIGNED, LICENSED OR INTENDED FOR USE
 * IN THE DESIGN, CONSTRUCTION, OPERATION OR MAINTENANCE OF ANY MILITARY FACILITY.
 */
import { AbstractTransactPlugin, TransactHookTypes } from '@wharfkit/session';

var ko = {
	complete: "거래 완료",
	click: "아래 버튼을 클릭하면 블록 탐색기에서 트랜잭션을 볼 수 있습니다.",
	visit: "익스플로러 방문",
	"no-result": "탐색기 링크를 생성할 수 없으며 결과가 없습니다.",
	"no-chain": "탐색기 링크를 생성할 수 없으며 체인 정의가 없습니다.",
	"no-explorer": "탐색기 링크를 생성 할 수 없으며 체인 정의가 탐색기를 정의하지 않았습니다.",
	"no-resolved": "탐색기 링크를 생성할 수 없으며 해결된 트랜잭션이 없습니다."
};
var zh = {
	complete: "交易完成",
	click: "點擊下面的按鈕在區塊瀏覽器中查看交易。",
	visit: "訪問探索者",
	"no-result": "無法生成資源管理員連結，沒有結果。",
	"no-chain": "無法生成資源管理員連結，沒有鏈定義。",
	"no-explorer": "無法生成資源管理員連結，鏈定義未定義資源管理員。",
	"no-resolved": "無法生成資源管理員連結，沒有已解決的事務。"
};
var defaultTranslations = {
	ko: ko,
	zh: zh
};

class TransactPluginExplorerLink extends AbstractTransactPlugin {
    constructor() {
        super(...arguments);
        this.id = 'transact-plugin-explorer-link';
        this.translations = defaultTranslations;
    }
    register(context) {
        context.addHook(TransactHookTypes.afterBroadcast, async (request, context, result) => {
            if (context.ui) {
                // Retrieve translation helper from the UI, passing the app ID
                const t = context.ui.getTranslate(this.id);
                // Ensure we have the data to build the link
                if (!result) {
                    throw new Error(t('no-result', {
                        default: 'Unable to generate explorer link, no result.',
                    }));
                }
                // Ensure we have a chain defined to base a link off
                if (!result.chain) {
                    throw new Error(t('no-chain', {
                        default: 'Unable to generate explorer link, no chain definition.',
                    }));
                }
                // Ensure the chain has an explorer defined on it
                if (!result.chain.explorer) {
                    throw new Error(t('no-explorer', {
                        default: 'Unable to generate explorer link, chain definition doesnt defined an explorer.',
                    }));
                }
                // Ensure we have a resolved transaction
                if (!result.resolved) {
                    throw new Error(t('no-resolved', {
                        default: 'Unable to generate explorer link, no resolved transaction.',
                    }));
                }
                // Update the status bar
                context.ui.status(t('complete', { default: 'Transaction Complete' }));
                // Prompt the user with the link to view the transaction
                const prompt = context.ui.prompt({
                    title: t('complete', {
                        default: 'Transaction Complete',
                    }),
                    body: t('click', {
                        default: 'Click the button below to view the transaction in a block explorer.',
                    }),
                    elements: [
                        {
                            type: 'link',
                            data: {
                                button: true,
                                label: t('visit', {
                                    default: 'Visit Explorer',
                                }),
                                href: this.getExplorerLink(context, result.chain, result.resolved.transaction.id),
                            },
                        },
                        {
                            type: 'close',
                        },
                    ],
                });
                // Return a dummy promise to block progress
                return prompt.then(async () => {
                    return new Promise((r) => r());
                });
            }
        });
    }
    getExplorerLink(context, chain, transaction) {
        if (!context.ui) {
            throw new Error('Unable to generate explorer link, no ui.');
        }
        if (!chain.explorer) {
            // Retrieve translation helper from the UI, passing the app ID
            const t = context.ui.getTranslate(this.id);
            throw new Error(t('no-explorer', {
                default: 'Unable to generate explorer link, chain definition doesnt defined an explorer.',
            }));
        }
        return chain.explorer?.url(String(transaction));
    }
}

export { TransactPluginExplorerLink };
//# sourceMappingURL=transact-plugin-explorerlink.m.js.map
